import { Alert, Box } from '@mui/material';
import React from 'react';
import { ArrayInput, FormDataConsumer, minValue, NumberInput, ReferenceArrayInput, required, SelectArrayInput, SelectInput, SimpleFormIterator, TextInput } from 'react-admin';
import { getChainId } from '../../../lib';
import { AttendantGoalType } from '../../../models';
import GoalSimulator from './GoalSimulator';

const MIX_INFO_TEXT = `A regra Mix define uma meta percentual sobre o volume abastecido: do total de litros de um grupo de combustíveis, qual % deve ser de um combustível específico.

Exemplo: "15% do volume de Gasolina deve ser Gasolina Aditivada"
→ Combustíveis Alvo: Gasolina Aditivada
→ Combustíveis Base: Gasolina Comum + Gasolina Aditivada
→ Meta: 15%

Se o frentista abastecer 1.000L de gasolina no dia e 150L forem aditivada, o resultado será 15%.`;

const FormTarget: React.FC<{ formData?: any }> = ({ formData }) => {

  return (
    <>
      <ArrayInput source="targets" label="Metas">
        <SimpleFormIterator disableReordering>
          <FormDataConsumer>
            {({ formData, scopedFormData }) => {
              const isMix = scopedFormData?.type === AttendantGoalType.MIX;
              const typesInUse = (formData.targets || []).map(target => target.type).filter(type => type);
              const availableTypes = AttendantGoalType.attendantGoalTypesChoices.filter(choice => !typesInUse.includes(choice.id) || choice.id === scopedFormData?.type);
              const chainId = getChainId();
              return (
                <Box display="flex" flexDirection="column" gap={1} pt={1.5}>

                  {/* Linha 1: campos básicos */}
                  <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap" alignItems="flex-start">
                    <SelectInput
                      source="type"
                      label="Tipo"
                      choices={availableTypes}
                      sx={{ minWidth: 200 }}
                      fullWidth={false}
                      validate={[required()]}
                    />
                    {isMix ? (
                      <NumberInput
                        source="minimumAmount"
                        label="Meta (%)"
                        min={0.01}
                        max={100}
                        step={0.1}
                        sx={{ width: 140 }}
                        fullWidth={false}
                        validate={[required(), minValue(0.01)]}
                      />
                    ) : (
                      <TextInput
                        source="minimumAmount"
                        label="Mínimo"
                        format={value => !value ? '0,00' : `${Number(value).toFixed(2)}`}
                        parse={value => !value ? 0 : +value.replace(/[^\d]/g, '') / 100}
                        sx={{ width: 160 }}
                        fullWidth={false}
                        validate={[required(), minValue(0.01)]}
                      />
                    )}
                    <TextInput
                      source="fixedPrice"
                      label="R$ fixo"
                      format={value => !value ? 'R$ 0,00' : `R$ ${Number(value).toFixed(2)}`}
                      parse={value => !value ? 0 : +value.replace(/[^\d]/g, '') / 100}
                      sx={{ width: 160 }}
                      fullWidth={false}
                    />
                    <TextInput
                      source="additionalUnit"
                      label="Unid. adicional"
                      format={value => !value ? '0,00' : `${Number(value).toFixed(2)}`}
                      parse={value => !value ? 0 : +value.replace(/[^\d]/g, '') / 100}
                      sx={{ width: 160 }}
                      fullWidth={false}
                    />
                    <TextInput
                      source="additionalPrice"
                      label="R$ adicional"
                      format={value => !value ? 'R$ 0,00' : `R$ ${Number(value).toFixed(2)}`}
                      parse={value => !value ? 0 : +value.replace(/[^\d]/g, '') / 100}
                      sx={{ width: 160 }}
                      fullWidth={false}
                    />
                  </Box>

                  {/* Linha 2 (Mix): seletores de combustível + info */}
                  {isMix && (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap" alignItems="flex-start">
                        <ReferenceArrayInput
                          source="numeratorFuelIds"
                          reference="fuels"
                          filter={chainId ? { chainId } : {}}
                        >
                          <SelectArrayInput
                            label="Combustíveis Alvo (Numerador)"
                            sx={{ minWidth: 280 }}
                            fullWidth={false}
                            validate={[required()]}
                          />
                        </ReferenceArrayInput>
                        <ReferenceArrayInput
                          source="denominatorFuelIds"
                          reference="fuels"
                          filter={chainId ? { chainId } : {}}
                        >
                          <SelectArrayInput
                            label="Combustíveis Base (Denominador)"
                            sx={{ minWidth: 280 }}
                            fullWidth={false}
                            validate={[required()]}
                          />
                        </ReferenceArrayInput>
                      </Box>
                      <Alert severity="info" sx={{ whiteSpace: 'pre-line', fontSize: '0.8rem', py: 0.5 }}>
                        {MIX_INFO_TEXT}
                      </Alert>
                    </Box>
                  )}

                  {/* Simulador de meta */}
                  {scopedFormData?.type && (
                    <GoalSimulator
                      targetType={scopedFormData.type}
                      minimumAmount={scopedFormData.minimumAmount}
                      index={(formData.targets || []).indexOf(scopedFormData)}
                      stationIds={formData.stationIds}
                      fuelIds={formData.fuelIds}
                      daysOfWeek={formData.daysOfWeek}
                      selectsAllPlaces={formData.selectsAllPlaces}
                      selectsAllFuels={formData.selectsAllFuels}
                      numeratorFuelIds={scopedFormData.numeratorFuelIds}
                      denominatorFuelIds={scopedFormData.denominatorFuelIds}
                    />
                  )}

                </Box>
              );
            }}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
}

export default FormTarget;
